import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import InputFile from '../InputFile';
import {
  submitFormData,
  getBase64,
  validateInputType,
  validateInputSize,
} from '../../helpers/form-api';
import { enquirySubtypes } from './FormVariables';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

class ConnectionServicesEnquiriesForm extends React.Component {
  initialFormData = {
    first_name: '',
    last_name: '',
    email: '',
    additional_email: '',
    network: '',
    enquiry_type: '',
    enquiry_subtype: '',
    address: '',
    nmi: '',
    message: '',
    b2b_number: '',
    enquiry_team: '',
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    formData: { ...this.initialFormData },
    errors: {},
    enquiryTypeOptions: [],
    enquirySubTypeOptions: [],
    enquirySubTypeNote: '',
  };

  attachmentInput1 = React.createRef();
  attachmentInputWrapper1 = React.createRef();
  attachmentInput2 = React.createRef();
  attachmentInputWrapper2 = React.createRef();
  attachmentInput3 = React.createRef();
  attachmentInputWrapper3 = React.createRef();
  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  containsNetwork = (networkName) => {
    return function (enquirySubType) {
      return enquirySubType.network.includes(networkName);
    };
  };

  containsEnquiryType = (enquiryName) => {
    return function (enquirySubType) {
      return enquirySubType.type.includes(enquiryName);
    };
  };

  handleSelectChange = (event, { name, value }) => {
    if (name === 'network') {
      // if the changed select is the network compile the enquiry type array
      // and place it into state with the following

      // discover which entries match with the selected network
      const matchedSubTypes = enquirySubtypes.filter(
        this.containsNetwork(value)
      );

      // resolve which unique enquiry types appear in the matchedEntries
      const matchedTypes = [
        ...new Set(matchedSubTypes.map((subType) => subType.type)),
      ];

      // Compiled into objects that the semantic UI coponent can accept
      // {
      //   text: '1. Initiate Work Request',
      //   key: 'Initiate Work Request',
      //   value: 'Initiate Work Request',
      // },
      const matchedTypesObj = matchedTypes.map((matchedType) => {
        return { text: matchedType, key: matchedType, value: matchedType };
      });

      this.setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
        },
        enquiryTypeOptions: matchedTypesObj,
      }));
    } else if (name === 'enquiry_type') {
      // if the changed select is the enquiry type compile the enquiry subtype
      // array from the currently selected network available in formData
      const matchedSubTypes = enquirySubtypes
        .filter(this.containsNetwork(this.state.formData.network))
        .filter(this.containsEnquiryType(value));
      // Compiled into objects that the semantic UI coponent can accept as above
      const matchedSubTypesObj = matchedSubTypes.map((matchedSubType) => {
        return {
          text: matchedSubType.label,
          key: matchedSubType.label,
          value: matchedSubType.label,
        };
      });

      this.setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          enquiry_subtype: '',
        },
        enquirySubTypeOptions:
          matchedSubTypesObj[0].key !== '' ? matchedSubTypesObj : [],
      }));
    } else if (name === 'enquiry_subtype') {
      // when the sub type is changed update the subtype note
      const selectedSubType = enquirySubtypes
        .filter(this.containsNetwork(this.state.formData.network))
        .filter((enquirySubtype) => {
          return enquirySubtype.label === value;
        });

      this.setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
          enquiry_team: selectedSubType[0].team,
        },
        enquirySubTypeNote: selectedSubType[0].description,
      }));
    } else {
      this.setState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [name]: value,
        },
      }));
    }
  };

  validateInputs = () => {
    // network required
    const {
      formData: {
        network,
        enquiry_type: enquiryType,
        enquiry_subtype: enquirySubType,
      },
    } = this.state;
    let valid = true;

    if (!network) {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          network: 'Required',
        },
      }));
      valid = false;
    } else {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          network: undefined,
        },
      }));
    }

    // enquiry type required
    if (!enquiryType) {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          enquiryType: 'Required',
        },
      }));
      valid = false;
    } else {
      this.setState((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          enquiryType: undefined,
        },
      }));
    }

    // only validate the subtypes if they exist
    if (this.state.enquirySubTypeOptions.length > 0) {
      // correct subtype required
      if (enquiryType !== '') {
        if (!enquirySubType) {
          this.setState((prevState) => ({
            ...prevState,
            errors: {
              ...prevState.errors,
              enquirySubType: 'Required',
            },
          }));
          valid = false;
        } else {
          this.setState((prevState) => ({
            ...prevState,
            errors: {
              ...prevState.errors,
              enquirySubType: undefined,
            },
          }));
        }
      }
    }

    let file1, file2, file3;
    if (this.attachmentInput1.current) {
      file1 = this.attachmentInput1.current.files[0];
      if (
        file1 &&
        (!validateInputType(file1.type) || !validateInputSize(file1.size, 5))
      ) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment1: 'FileType',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment1: undefined,
          },
        }));
      }
    }

    if (this.attachmentInput2.current) {
      file2 = this.attachmentInput2.current.files[0];
      if (
        file2 &&
        (!validateInputType(file2.type) || !validateInputSize(file2.size, 5))
      ) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment2: 'FileType',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment2: undefined,
          },
        }));
      }
    }
    if (this.attachmentInput3.current) {
      file3 = this.attachmentInput3.current.files[0];
      if (
        file3 &&
        (!validateInputType(file3.type) || !validateInputSize(file3.size, 5))
      ) {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment3: 'FileType',
          },
        }));
        valid = false;
      } else {
        this.setState((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            attachment3: undefined,
          },
        }));
      }
    }

    return valid;
  };

  handleSubmit = async () => {
    try {
      this.setState({ success: null, error: null, submitting: true });

      const valid = this.validateInputs();
      if (!valid) {
        this.setState({
          error: 'Missing field.',
          success: null,
          submitting: false,
        });
        return;
      }

      if (this.state.captcha) {
        let file1, file2, file3;
        if (this.attachmentInput1.current) {
          file1 = this.attachmentInput1.current.files[0];
        }
        if (this.attachmentInput2.current) {
          file2 = this.attachmentInput2.current.files[0];
        }
        if (this.attachmentInput3.current) {
          file3 = this.attachmentInput3.current.files[0];
        }
        let attachment1, attachment2, attachment3;
        if (file1) {
          attachment1 = {
            content: await getBase64(file1),
            name: file1.name,
          };
        }
        if (file2) {
          attachment2 = {
            content: await getBase64(file2),
            name: file2.name,
          };
        }
        if (file3) {
          attachment3 = {
            content: await getBase64(file3),
            name: file3.name,
          };
        }

        const formData = {
          ...this.state.formData,
          attachment1,
          attachment2,
          attachment3,
          captchaResponse: this.state.captcha,
        };

        const response = await submitFormData(
          'connection_services_enquiries',
          formData
        );

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: 'Success',
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });
          if (this.attachmentInputWrapper1.current) {
            this.attachmentInputWrapper1.current.clear();
          }
          if (this.attachmentInputWrapper2.current) {
            this.attachmentInputWrapper2.current.clear();
          }
          if (this.attachmentInputWrapper3.current) {
            this.attachmentInputWrapper3.current.clear();
          }
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const enquiryNetworks = [
      {
        text: 'CitiPower',
        value: 'CitiPower',
        key: 'CitiPower',
      },
      {
        text: 'Powercor',
        value: 'Powercor',
        key: 'Powercor',
      },
    ];

    const {
      first_name,
      last_name,
      email,
      additional_email,
      network,
      enquiry_type,
      enquiry_subtype,
      address,
      nmi,
      message,
      b2b_number,
    } = this.state.formData;
    const {
      errors: {
        network: networkError,
        enquiryType: enquiryTypeError,
        enquirySubType: subtypeError,
        attachment1: attachment1Error,
        attachment2: attachment2Error,
        attachment3: attachment3Error,
      },
    } = this.state;

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form
                onSubmit={this.handleSubmit}
                data-testid="connection-services-enquiries-form"
              >
                <h4>Retailer employee details</h4>
                <Form.Input
                  label="First name"
                  required
                  name="first_name"
                  type="text"
                  value={first_name}
                  onChange={this.handleInputChange}
                  id="form-first-name"
                />

                <Form.Input
                  label="Last name"
                  required
                  type="text"
                  name="last_name"
                  value={last_name}
                  onChange={this.handleInputChange}
                  id="form-last-name"
                />

                <Form.Input
                  label="Email"
                  required
                  type="email"
                  name="email"
                  value={email}
                  onChange={this.handleInputChange}
                  id="form-email"
                />

                <Form.Input
                  label="Additional Email"
                  type="email"
                  name="additional_email"
                  value={additional_email}
                  onChange={this.handleInputChange}
                  id="additional-email"
                />
                <div className="form-field__description">
                  A copy of your submission will also be sent to this address{' '}
                </div>

                <h4>Connection and enquiry details</h4>
                <Form.Select
                  label="Network"
                  required
                  options={enquiryNetworks}
                  placeholder="Please choose"
                  value={network}
                  name="network"
                  onChange={this.handleSelectChange}
                  id="form-network"
                  error={networkError != null}
                />

                <Form.Select
                  label="Enquiry type"
                  required
                  options={this.state.enquiryTypeOptions}
                  placeholder="Please choose"
                  name="enquiry_type"
                  value={enquiry_type}
                  onChange={this.handleSelectChange}
                  id="form-enquiry-type"
                  error={enquiryTypeError != null}
                />

                {this.state.enquirySubTypeOptions.length > 0 && (
                  <>
                    <Form.Select
                      label="Subtype"
                      required
                      options={this.state.enquirySubTypeOptions}
                      placeholder="Please choose"
                      name="enquiry_subtype"
                      value={enquiry_subtype}
                      onChange={this.handleSelectChange}
                      id="form-subtype"
                      error={subtypeError != null}
                    />
                    <sub>{this.state.enquirySubTypeNote}</sub>
                  </>
                )}

                <Form.Input
                  label="Address"
                  type="text"
                  name="address"
                  value={address}
                  onChange={this.handleInputChange}
                  id="form-address"
                />

                <Form.Field>
                  <Form.Input
                    label="NMI"
                    type="text"
                    name="nmi"
                    value={nmi}
                    onChange={this.handleInputChange}
                    id="form-nmi"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label="B2B Number"
                    type="text"
                    name="b2b_number"
                    value={b2b_number}
                    onChange={this.handleInputChange}
                    id="form-b2b-number"
                  />
                </Form.Field>

                <Form.Field>
                  <Form.TextArea
                    label="Message"
                    type="text"
                    name="message"
                    value={message}
                    onChange={this.handleInputChange}
                    id="form-message"
                  />
                </Form.Field>

                <InputFile
                  label="File Attachment 1"
                  name="file_1"
                  text="No file chosen"
                  restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                  id="form-file-1"
                  maxFileSize={1}
                  refProp={this.attachmentInput1}
                  ref={this.attachmentInputWrapper1}
                  error={attachment1Error != null}
                />

                <InputFile
                  label="File Attachment 2"
                  name="file_2"
                  text="No file chosen"
                  restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                  id="form-file-2"
                  maxFileSize={1}
                  refProp={this.attachmentInput2}
                  ref={this.attachmentInputWrapper2}
                  error={attachment2Error != null}
                />

                <InputFile
                  label="File Attachment 3"
                  name="file_3"
                  text="No file chosen"
                  restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                  id="form-file-3"
                  maxFileSize={1}
                  refProp={this.attachmentInput3}
                  ref={this.attachmentInputWrapper3}
                  error={attachment3Error != null}
                />

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />

                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>

                {this.state.success ? (
                  <div className="success-message">{this.state.success}</div>
                ) : null}
                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}

export default ConnectionServicesEnquiriesForm;
